import BaseDashboardCard from "../BaseDashboardCard";
import { t } from "i18next";
import Box from "../Box";
import { CommercialUnitDataWithBrand } from "../../types/commercial";
import PieChart from "../PieChart";
import styled from "styled-components";
import DeltaBadge from "../DeltaBadge";
import MrclText from "../marcel/MrclText";
import useBreakPoint from "../../utils/useBreakPoint";
import { formatCurrency, formatPercentage } from "../../utils/numberUtils";

type TurnoverTriptychCardProps = {
  title: string;
  isUnavailable: boolean;
  isLoading: boolean;
  openInformationModal: () => void;
  triptychTurnoverRates: (CommercialUnitDataWithBrand & { evol: number | null })[];
};

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Tr = styled.tr`
  height: 40px;

  @media only screen and (max-width: 932px) and (orientation: landscape) {
    height: 26px;
  }
`;

const Th = styled.th<{ $textAlign?: string; $width?: string }>`
  text-align: ${(props) => props?.$textAlign};
  width: ${(props) => props?.$width};
`;

const Td = styled.td<{ $textAlign?: string }>`
  text-align: ${(props) => props?.$textAlign};
  border-bottom: 1px solid #d9d9d9;
  padding-block: 4px;

  @media only screen and (max-width: 932px) and (orientation: landscape) {
    padding-block: 1px;
  }
`;

const BoxContainer = styled(Box)`
  gap: ${(props) => `${props.theme.space?.[1]}px`};
`;

const Text = styled(MrclText)`
  font-family: system-ui;
`;

const Marque = styled.span`
  font-family: system-ui;
  font-weight: 700;
  font-size: caption;
`;

const Bull = styled.span`
  border-radius: 4px;
  width: 8px;
  height: 8px;
  display: inline-block;
  margin-right: 8px;
`;

const CardContentGrid = styled(Box)`
  flex: 1 !important;
  display: flex !important;
  flex-direction: column !important;
  justify-content: center !important;
  align-items: center !important;
  width: 100%;
  margin: 0 !important;

  @media only screen and (max-width: 932px) and (orientation: landscape) {
    padding: 0 !important;
    margin: auto 0 !important; /* Centers grid vertically inside the available height */
    display: grid !important;
    grid-template-columns: 1fr 2fr !important;
    align-content: center !important;
    align-items: center !important;
    align-self: center !important;
    height: 100% !important;
  }
`;

const ChartWrapper = styled(Box)`
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  width: 100%;

  @media only screen and (max-width: 932px) and (orientation: landscape) {
    padding: 0 !important;
    margin: 0 !important;
    align-self: center !important;
  }
`;

const TableWrapper = styled(Box)`
  width: 100%;

  @media only screen and (max-width: 932px) and (orientation: landscape) {
    margin-left: 10px !important;
    margin-bottom: 0px !important;
    align-self: center !important;
  }
`;

const StyledBaseDashboardCard = styled(BaseDashboardCard)<{ isLoading?: boolean; isUnavailable?: boolean }>`
  display: flex !important;
  flex-direction: column !important;
  height: 300px !important;
  min-height: 300px !important;
  max-height: 300px !important;

  @media only screen and (max-width: 932px) and (orientation: landscape) {
    padding: 4px 8px !important; /* Tight spacing for standard mobile landscape devices */
    height: 180px !important;
    min-height: 180px !important;
    max-height: 180px !important;
    flex-shrink: 0 !important;

    /* Tighten header spacing to remove offset before chart content */
    [class*="header"] {
      margin-top: 0 !important;
      margin-bottom: 0 !important;
      padding-top: 2px !important;
      padding-bottom: 0 !important;
    }

    ${({ isLoading, isUnavailable }) => !isLoading && !isUnavailable && `
      & > div,
      .c-card,
      [class*="content"],
      [class*="body"] {
        display: flex !important;
        flex-direction: column !important;
        justify-content: center !important; /* Vertically center card contents */
        align-items: center !important;
        padding: 0 !important;
        margin: 0 !important;
        flex: 1 !important;
        height: 100% !important;
      }
    `}
  }
`;

const TurnoverTriptychCard = ({
  title,
  openInformationModal,
  triptychTurnoverRates,
  ...rest
}: TurnoverTriptychCardProps) => {
  const colors = ["#EECA34", "#64BDC6", "#2B72FB"];
  const isBreak = useBreakPoint();
  const isLandscape =
    typeof window !== "undefined" &&
    window.matchMedia("(max-width: 932px) and (orientation: landscape)").matches;

  const data: { key: string; y: number }[] =
    triptychTurnoverRates?.map((x) => ({ key: "", y: (x.turnoverRate ?? 0) * 100 })) || [];

  return (
    <StyledBaseDashboardCard
      title={t(title)}
      size="big"
      hasModal={true}
      openInformationModal={openInformationModal}
      {...rest}
      importedDateTime={
        triptychTurnoverRates?.length ? triptychTurnoverRates[0]?.importedDateTime : undefined
      }
    >
      <CardContentGrid py={0}>
        <ChartWrapper minWidth={0} pt={0} pb={0}>
          <PieChart
            colors={colors}
            data={data}
            height={isLandscape ? 120 : 200}
            width={isLandscape ? 120 : 300}
            radius={isLandscape ? 50 : 100}
            innerRadius={isLandscape ? 30 : 65}
            origin={isLandscape ? { x: 60, y: 60 } : { x: 150, y: 100 }}
            labelX={isLandscape ? 30 : 65}
            labelY={isLandscape ? 30 : 65}
            showTooltip={false}
          />
        </ChartWrapper>
        <TableWrapper minWidth={0}>
          <Table>
            <thead>
              <Tr>
                <Th $textAlign="left" $width="30%" />
                <Th $textAlign="left" $width="20%">
                  <Text bold size={"s"} tagStyle={"p"}>
                    {t("commercial.part")}
                  </Text>
                </Th>
                <Th $textAlign="left" $width="25%">
                  <Text bold size={"m"} tagStyle={"p"}>
                    {t("commercial.ca")}
                  </Text>
                </Th>
                <Th $textAlign="left">
                  <Text bold size={"m"} tagStyle={"p"}>
                    {"vs "} {t("commercial.n_1")}
                  </Text>
                </Th>
              </Tr>
            </thead>
            <tbody>
              {triptychTurnoverRates &&
                triptychTurnoverRates.map((triptych, index) => {
                  const color = index < colors.length ? colors[index] : "";
                  return (
                    <Tr key={"part" + triptych.brand}>
                      <Td $textAlign="left">
                        <Bull style={{ backgroundColor: color }} />
                        <Marque>
                          {isBreak === "phone"
                            ? triptych.brand
                            : t(`commercial.${triptych.brand}`)}
                        </Marque>
                      </Td>
                      <Td $textAlign="left">
                        <Text>
                          {formatPercentage((triptych.turnoverRate ?? 0) * 100, "%")}
                        </Text>
                      </Td>
                      <Td $textAlign="left">
                        <Text>{formatCurrency(triptych.turnover ?? 0)}</Text>
                      </Td>
                      <Td $textAlign="left">
                        <BoxContainer display={"flex"} alignItems={"center"} data-testid="Ctag">
                          {!triptych.evol || isNaN(triptych.evol) ? (
                            "-"
                          ) : (
                            <DeltaBadge value={triptych.evol} showIcon={false} showSign={true} />
                          )}
                        </BoxContainer>
                      </Td>
                    </Tr>
                  );
                })}
            </tbody>
          </Table>
        </TableWrapper>
      </CardContentGrid>
    </StyledBaseDashboardCard>
  );
};

export default TurnoverTriptychCard;